import type { ReactNode } from 'react';
import { useT } from '@/shared/lib/i18n';
import { AdaptiveDialog } from './AdaptiveDialog';
import { BrandMark } from './BrandMark';
import { HandKicker } from './HandKicker';
import { PhaseTag } from './PhaseTag';
import { Pin } from './Pin';

export type PauseMenuProps = {
  open: boolean;
  onResume: () => void;
  onShowRules: () => void;
  onShowRecap?: () => void;
  hasRecap?: boolean;
  onQuit: () => void;
};

export const PauseMenu = ({
  open,
  onResume,
  onShowRules,
  onShowRecap,
  hasRecap = false,
  onQuit,
}: PauseMenuProps) => {
  const t = useT();

  return (
    <AdaptiveDialog
      open={open}
      onClose={onResume}
      ariaLabel={t('pause.title')}
      closeLabel={t('pause.resume.label')}
      surface="bare"
      hideHandle
    >
      <div className="paper-dossier relative flex flex-col gap-5 px-7 py-6 text-ink">
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />

        <header className="flex flex-col gap-1">
          <PhaseTag size={11} color="rgba(0,0,0,.5)">
            {t('pause.eyebrow')}
          </PhaseTag>
          <BrandMark size={'clamp(36px, 4vw, 46px)'} className="mt-1">
            {t('pause.title')}
          </BrandMark>
          <HandKicker size={20} color="var(--color-pencil)" rot={0} className="mt-2">
            — {t('pause.sub')}
          </HandKicker>
        </header>

        <ul className="flex flex-col">
          <ActionRow
            onClick={onResume}
            tile={<PlayGlyph />}
            tileBg="var(--color-pop)"
            label={t('pause.resume.label')}
            sub={t('pause.resume.sub')}
          />
          {hasRecap && onShowRecap && (
            <ActionRow
              onClick={onShowRecap}
              tile="📋"
              tileBg="var(--color-civ)"
              tileColor="white"
              label={t('pause.recap.label')}
              sub={t('pause.recap.sub')}
            />
          )}
          <ActionRow
            onClick={onShowRules}
            tile="?"
            tileBg="var(--color-white-gold)"
            label={t('pause.rules.label')}
            sub={t('pause.rules.sub')}
          />
          <ActionRow
            onClick={onQuit}
            tile="✕"
            tileBg="var(--color-und)"
            tileColor="white"
            label={t('pause.quit.label')}
            sub={t('pause.quit.sub')}
            labelColor="var(--color-und)"
          />
        </ul>
      </div>
    </AdaptiveDialog>
  );
};

const ActionRow = ({
  onClick,
  tile,
  tileBg,
  tileColor,
  label,
  sub,
  labelColor,
}: {
  onClick: () => void;
  tile: ReactNode;
  tileBg: string;
  tileColor?: string;
  label: string;
  sub: string;
  labelColor?: string;
}) => (
  <li className="border-ink/85 border-b last:border-b-0">
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 py-3 text-left"
    >
      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center border-[1.5px] border-ink font-display font-extrabold text-[14px]"
        style={{ background: tileBg, color: tileColor ?? 'var(--color-ink)' }}
      >
        {tile}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block font-type text-[15px] tracking-[0.04em]"
          style={{ color: labelColor ?? 'var(--color-ink)' }}
        >
          {label}
        </span>
        <span className="mt-0.5 block text-[11px] text-ink/55">{sub}</span>
      </span>
      <span aria-hidden className="font-type text-[18px] text-ink/55 leading-none">
        ›
      </span>
    </button>
  </li>
);

const PlayGlyph = () => (
  <svg width="11" height="12" viewBox="0 0 11 12" role="img" aria-hidden>
    <title>play</title>
    <path d="M1 1 L10 6 L1 11 Z" fill="currentColor" />
  </svg>
);
